import cx from "classnames";
import { Button, HeartIcon, Text } from "@olivahealth/oli-ui";

import tokens from "@olivahealth/oli-ui/theme/tokens";
import { ResourceTrackingProperties } from "../../../types/tracking";
import useTranslation from "../../../hooks/useTranslation";
import useMarkAsFavouriteButton from "./useMarkAsFavouriteButton";

interface IProps {
  resourceId: string;
  isFavourite?: boolean;
  variant?: "full" | "icon" | "button";
  trackingProperties?: ResourceTrackingProperties;
  onFavouriteToggle?: () => void;
}

export default function MarkAsFavouriteButton({
  resourceId,
  isFavourite = false,
  variant = "icon",
  trackingProperties,
  onFavouriteToggle,
}: IProps) {
  const {
    data: { isFavourite: isFavouriteState },
    effects: { toggleFavouriteStatus },
  } = useMarkAsFavouriteButton({
    resourceId,
    isFavourite,
    trackingProperties,
    onFavouriteToggle,
  });

  const { t } = useTranslation("common", {
    keyPrefix: "markAsFavouriteButton",
  });

  const styledHeartIcon = (
    <HeartIcon
      size={22}
      className={cx({
        "transform group-hover:scale-110": variant !== "button",
        "mr-2": variant === "full",
      })}
      color={variant === "icon" && isFavouriteState ? "green-300" : "white"}
      variant={isFavouriteState ? "filled" : "outline"}
    />
  );

  if (variant === "button") {
    return (
      <Button
        iconLeft={styledHeartIcon}
        variant="secondaryInverse"
        onClick={toggleFavouriteStatus}
        width="full"
      >
        <Text color="white">
          {t(isFavouriteState ? "title.favourited" : "title.add")}
        </Text>
      </Button>
    );
  }

  if (variant === "icon") {
    return (
      <Button
        size="xs"
        variant="transparent"
        onClick={(e) => toggleFavouriteStatus(e)}
      >
        <HeartIcon
          variant={isFavouriteState ? "filled" : "outline"}
          size={24}
          color={isFavouriteState ? tokens.colors["green"][300] : "white"}
        />
      </Button>
    );
  }

  return (
    <button onClick={(e) => toggleFavouriteStatus(e)} className="group rebrand">
      {styledHeartIcon}
      {variant === "full" && (
        <Text color="white">
          {t(isFavouriteState ? "title.remove" : "title.add")}
        </Text>
      )}
    </button>
  );
}
